import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PenNibIcon as PenNib, PlugsConnectedIcon as PlugsConnected, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, ArrowSquareOutIcon as ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

export function DocuSignConnectorCard() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/integrations/docusign/config").then(({ data }) => setCfg(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openModal = () => {
    setForm({
      enabled: cfg?.enabled || false, environment: cfg?.environment || "demo",
      integration_key: cfg?.integration_key || "", user_id: cfg?.user_id || "",
      account_id: cfg?.account_id || "", rsa_private_key: "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/integrations/docusign/config", form);
      setCfg(data); setOpen(false); toast.success("Configurazione DocuSign salvata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integrations/docusign/test");
      toast.success(`Connessione riuscita · account ${data.account_id}`); load();
    } catch (e) {
      const d = e.response?.data?.detail || "";
      if (String(d).startsWith("CONSENT_REQUIRED")) toast.error("Consenso richiesto: apri l'URL di consenso qui sotto e autorizza l'app, poi riprova.");
      else toast.error(formatApiErrorDetail(d));
      load();
    } finally { setTesting(false); }
  };

  if (!cfg) return null;
  const st = cfg.last_test_status;
  return (
    <Card className="rounded-sm p-5 shadow-none border-border" data-testid="docusign-connector-card">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-sm bg-indigo-50 flex items-center justify-center"><PenNib size={20} className="text-indigo-600" weight="fill" /></div>
          <div>
            <h3 className="font-semibold text-sm">DocuSign eSignature</h3>
            <p className="text-xs text-muted-foreground">Firma elettronica dei PDF da parte dei clienti (JWT Grant · {cfg.environment})</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {cfg.enabled && <Badge variant="outline" className="rounded-sm text-[10px] bg-green-50 text-green-700">Attivo</Badge>}
          {st === "success" && <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle size={14} weight="fill" /> Connesso</span>}
          {st === "error" && <span className="flex items-center gap-1 text-xs text-red-600"><XCircle size={14} weight="fill" /> Errore</span>}
        </div>
      </div>
      {cfg.last_test_message && <p className="text-[11px] text-muted-foreground mb-2">{cfg.last_test_message}</p>}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={openModal} className="rounded-sm" data-testid="ds-configura-btn">Configura</Button>
        <Button variant="outline" onClick={test} disabled={testing} className="rounded-sm gap-2" data-testid="ds-test-btn">
          <PlugsConnected size={16} /> {testing ? "Verifica..." : "Verifica connessione"}
        </Button>
        {cfg.consent_url && (
          <a href={cfg.consent_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-indigo-600 self-center hover:underline" data-testid="ds-consent-link">
            <ArrowSquareOut size={14} /> Concedi consenso JWT (una tantum)
          </a>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Configura DocuSign</DialogTitle>
            <DialogDescription>Credenziali dell'app DocuSign (Settings → Apps and Keys). Salvate in modo cifrato.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between"><Label className="text-xs">Connettore attivo</Label>
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} data-testid="ds-enabled-switch" /></div>
            <div><Label className="text-xs">Ambiente</Label>
              <Select value={form.environment} onValueChange={(v) => setForm((f) => ({ ...f, environment: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="ds-env-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="demo">Demo / Sandbox (account-d.docusign.com)</SelectItem><SelectItem value="production">Produzione (account.docusign.com)</SelectItem></SelectContent>
              </Select></div>
            <div><Label className="text-xs">Integration Key (Client ID)</Label><Input value={form.integration_key} onChange={(e) => setForm((f) => ({ ...f, integration_key: e.target.value }))} className="rounded-sm" data-testid="ds-integration-key" /></div>
            <div><Label className="text-xs">User ID (API Username mittente)</Label><Input value={form.user_id} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))} className="rounded-sm" data-testid="ds-user-id" /></div>
            <div><Label className="text-xs">Account ID (opzionale)</Label><Input value={form.account_id} onChange={(e) => setForm((f) => ({ ...f, account_id: e.target.value }))} className="rounded-sm" placeholder="Verificato automaticamente in fase di test" /></div>
            <div><Label className="text-xs">RSA Private Key (PEM)</Label>
              <Textarea value={form.rsa_private_key} onChange={(e) => setForm((f) => ({ ...f, rsa_private_key: e.target.value }))} rows={5} className="rounded-sm font-mono text-[11px]" data-testid="ds-rsa-key" placeholder={cfg.rsa_private_key_configured ? "•••• chiave già configurata — incolla per sostituirla ••••" : "-----BEGIN RSA PRIVATE KEY-----\n..."} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annulla</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="ds-save-btn">{saving ? "Salvataggio…" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
